/** Sweet-state hooks for the spaces store. */

import {
	createActionsHook,
	createHook,
	createStateHook,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type HookStateFunction,
} from 'react-sweet-state';

import { spaceStore } from './store';
import type { SpaceStoreActions } from './store';
import type {
	Space,
	SpaceConversationsResponse,
	SpaceMemoryItem,
	SpaceResponse,
	SpaceStoreState,
	SmartContextLinkRecommendation,
} from './types';

type Actions = BoundActions<SpaceStoreState, SpaceStoreActions>;

/** [state, actions] — mirrors `useStagingArea` etc. Compat shape. */
export const useSpaceStoreCombined: HookFunction<SpaceStoreState, Actions> = createHook(spaceStore);

/** State only — replaces the React-context `useSpaceStore()`. */
export const useSpaceStoreState: HookStateFunction<SpaceStoreState> = createStateHook(spaceStore);

/** Actions only — replaces the React-context `useSpaceStoreActions()`. */
export const useSpaceStoreActions: HookActionsFunction<Actions> = createActionsHook(spaceStore);

// --- Selectors -----------------------------------------------------------

export const useSpaces: HookStateFunction<Space[]> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState) => state.spaces,
});

export const useSpacesLoading: HookStateFunction<boolean> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState) => state.isListLoading,
});

export const useSpacesError: HookStateFunction<Error | null> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState) => state.listError,
});

export const useHasFetchedSpaces: HookStateFunction<boolean> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState) => state.hasFetchedList,
});

/** Aggregated conversationIds across all spaces. Stable array reference — pre-computed in store state. */
export const useSpaceConversationIds: HookStateFunction<string[]> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState) => state.conversationIds,
});

// --- Per-id detail selectors --------------------------------------------

/**
 * Per-id detail-shape selector. Returns `undefined` until the detail has been
 * fetched for the given id.
 */
export const useSpaceById: HookStateFunction<SpaceResponse | undefined, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) => state.spaceDetails.get(spaceId),
	},
);

export const useSpaceDetailLoading: HookStateFunction<boolean, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) =>
			state.detailLoading.get(spaceId) ?? false,
	},
);

export const useSpaceDetailError: HookStateFunction<Error | null, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) => state.detailErrors.get(spaceId) ?? null,
	},
);

// --- Per-id conversations selectors -------------------------------------

export const useSpaceConversationsPage: HookStateFunction<
	SpaceConversationsResponse | null,
	string
> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState, spaceId: string) =>
		state.spaceConversations.get(spaceId) ?? null,
});

export const useSpaceConversationsLoading: HookStateFunction<boolean, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) =>
			state.conversationsLoading.get(spaceId) ?? false,
	},
);

export const useSpaceConversationsError: HookStateFunction<Error | null, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) =>
			state.conversationsErrors.get(spaceId) ?? null,
	},
);

// --- Per-id memory selectors --------------------------------------------

export const useSpaceMemories: HookStateFunction<SpaceMemoryItem[], string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) => state.spaceMemories.get(spaceId) ?? [],
	},
);

export const useSpaceMemoriesLoading: HookStateFunction<boolean, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) =>
			state.memoriesLoading.get(spaceId) ?? false,
	},
);

export const useSpaceMemoriesError: HookStateFunction<Error | null, string> = createStateHook(
	spaceStore,
	{
		selector: (state: SpaceStoreState, spaceId: string) =>
			state.memoriesErrors.get(spaceId) ?? null,
	},
);

// --- Per-id smart context selectors -------------------------------------

export const useSmartContextLinkRecommendations: HookStateFunction<
	SmartContextLinkRecommendation[],
	string
> = createStateHook(spaceStore, {
	selector: (state: SpaceStoreState, spaceId: string) =>
		state.smartContextLinkRecommendations.get(spaceId) ?? [],
});
